import { getTerm } from "./complianceCopy";
import { isDTS } from "./platform";

/**
 * Player-facing messages for the entry flows — placing, listing, and settling
 * entries early — in both platform wordings.
 *
 * These were scattered as inline literals across the slip, the bet builder,
 * the open-entries table and the early-settlement modal, and most of them
 * reached DTS players unchanged because they lived in validation objects,
 * template literals and error states rather than in JSX. Keeping them here
 * means one toast, inline error and status banner cannot drift apart, and the
 * compliance scanner has one place to verify.
 *
 * PPH strings are the originals, byte-for-byte. DTS strings are built from the
 * admin-configured vocabulary on every call (never cached), so a rename in the
 * admin panel reaches them without a reload.
 */

const entry = () => getTerm("entry");
const entryLower = () => getTerm("entry", { case: "lower" });
const entriesLower = () => getTerm("entry", { plural: true, case: "lower" });
const settlement = () => getTerm("earlySettlement");
const settlementLower = () => getTerm("earlySettlement", { case: "lower" });

// ─── Slip validation and submission ───────────────────────────────────────────

export const amountEmptyMessage = () =>
  isDTS() ? `${entry()} Amount shouldn't be empty` : "Bet Amount shouldn't be empty";

/** Same message as amountEmptyMessage; the older slip validators spell the PPH copy with a lowercase "bet". */
export const slipAmountEmptyMessage = () =>
  isDTS() ? `${entry()} Amount shouldn't be empty` : "bet Amount shouldn't be empty";

export const amountPositiveMessage = () =>
  isDTS() ? `${entry()} Amount should be greater than 0` : "Bet Amount should be greater than 0";

export const invalidEntryTypeMessage = (type) =>
  isDTS() ? `Invalid ${entryLower()} type: ${type}` : `Invalid bet type: ${type}`;

export const entryValidationFailedMessage = () =>
  isDTS()
    ? `${entry()} validation failed. Please check your selections and try again.`
    : "Bet validation failed. Please check your selections and try again.";

export const fixHighlightedFieldsMessage = () =>
  isDTS()
    ? `Please fix the highlighted ${entryLower()} fields before submitting.`
    : "Please fix the highlighted bet fields before submitting.";

export const parlayMinLegsMessage = (minLegs) =>
  isDTS()
    ? `Select at least ${minLegs} legs for a combo ${entryLower()}.`
    : `Select at least ${minLegs} legs for a parlay bet.`;

/** Same-event rule for parlays only (Site Notes Function Rules). */
export const sameEventComboMessage = () =>
  isDTS()
    ? "Only one pick from each event is allowed in a combo."
    : "Only one pick from each event is allowed in a parlay.";

export const acceptOddsChangesLabel = () => "Accept Odds Changes";

export const acceptOddsChangesInfo = () =>
  isDTS()
    ? "Off: if the board line moves before you submit, the entry is rejected and the slip shows the new line. On: submit at the current board line. A Target pick keeps the spread you chose and fills when that number is on the board, at the price then showing."
    : "Off: if odds or points move before you submit, the bet is rejected and the slip shows the new line. On: submit at the current board line. A call bet keeps the spread you chose and fills when that number is on the board, at the price then showing.";

export const oddsChangedMessage = () =>
  isDTS()
    ? "Lines have changed. Review the updated numbers on your slip, or turn on Accept Odds Changes to place at the current board price."
    : "Odds have changed. Review the updated lines on your slip, or turn on Accept Odds Changes to place at the current board price.";

export const parlayPrizeUnavailableMessage = () =>
  isDTS()
    ? `Combo ${getTerm("prize", { case: "lower" })} could not be calculated. Re-enter your stake amount.`
    : "Parlay payout could not be calculated. Re-enter your stake amount.";

export const loginToPlaceMessage = () =>
  isDTS() ? `Please login to place your ${entryLower()}` : "Please login to place a bet";

export const teaserMoneylineMessage = () =>
  isDTS()
    ? `To Win ${entriesLower()} cannot be part of a +Points Combo.`
    : "Moneyline (H2H) bets cannot be part of a teaser.";

export const teaserMoneylineSelectionsMessage = () =>
  isDTS()
    ? "To Win selections cannot be part of a +Points Combo. Please select Spreads or Totals."
    : "Moneyline (M-Line) selections cannot be part of a teaser. Please select Spreads or Totals.";

export const teaserMoneylineBlockedMessage = () =>
  isDTS()
    ? "Cannot perform +Points Combo with To Win selections. Please remove them."
    : "Cannot perform teaser bet with M-Line selections. Please remove them.";

export const entryPlacedMessage = () =>
  isDTS() ? `${entry()} Placed Successfully` : "Bet Placed Successfully";

export const placeEntryFailedMessage = () =>
  isDTS() ? `Unable to place your ${entryLower()}. Please try again.` : "Unable to place bet. Please try again.";

/** Label before a slip's potential prize amount. */
export const prizeAmountLabel = () => (isDTS() ? `${getTerm("prize")}:` : "Payout:");

// ─── Target pick (Call entries) ───────────────────────────────────────────────

export const callEntriesSubmittedMessage = (count) => {
  if (count > 1) {
    return isDTS()
      ? `${count} Target picks submitted — pending activation when markets hit your targets`
      : `${count} call bets submitted — pending activation when markets hit your targets`;
  }
  return isDTS()
    ? "Target pick submitted — pending activation when market hits your target"
    : "Call bet submitted — pending activation when market hits your target";
};

export const submitCallEntriesLabel = (count) => {
  if (count > 1) {
    return isDTS() ? "Submit Target picks" : "Submit Call Bets";
  }
  return isDTS() ? "Submit Target pick" : "Submit Call Bet";
};

// ─── Loading entries ──────────────────────────────────────────────────────────

/** `retry` adds the "Please try again." the network-failure variant carries. */
export const loadCallEntriesFailedMessage = ({ retry = false } = {}) => {
  if (isDTS()) return `Unable to load Target picks.${retry ? " Please try again." : ""}`;
  return retry ? "Unable to load call bets. Please try again." : "Unable to load call bets.";
};

export const loadActiveEntriesFailedMessage = ({ retry = false } = {}) => {
  if (isDTS()) return `Unable to load active ${entriesLower()}.${retry ? " Please try again." : ""}`;
  return retry ? "Unable to load active bets. Please try again." : "Unable to load active bets.";
};

export const loadEntryHistoryFailedMessage = () =>
  isDTS() ? `Could not load ${entryLower()} history.` : "Could not load bet history.";

export const entrySnapshotNotice = () =>
  isDTS()
    ? `Showing your placed ${entryLower()} snapshot — use Refresh board when lines are available.`
    : "Showing your placed bet snapshot — use Refresh board when lines are available.";

/** Modal title prefix; the ticket id follows it. */
export const entryDetailsTitle = () =>
  isDTS() ? `${getTerm("entry", { case: "upper" })} DETAILS FOR TICKET #` : "WAGER DETAILS FOR TICKET #";

// ─── Early settlement (PPH: cash out) ─────────────────────────────────────────

export const settlementActionLabel = () => (isDTS() ? settlement() : "Cash Out");

export const settlementTitle = (ticketId) =>
  isDTS() ? `${settlement()} — Ticket #${ticketId}` : `Cash Out — Ticket #${ticketId}`;

/** Accessible name of an open entry's settle button. `amountText` is already formatted. */
export const settlementButtonAriaLabel = (ticketId, amountText) => {
  if (isDTS()) {
    return amountText
      ? `${settlement()} for ticket ${ticketId}: ${amountText}`
      : `${settlement()} for ticket ${ticketId}`;
  }
  return amountText ? `Cash out ticket ${ticketId} for ${amountText}` : `Cash out ticket ${ticketId}`;
};

export const settlementUnavailableHeading = () =>
  isDTS() ? `${settlement()} unavailable` : "Cash out unavailable";

export const settlementNotAvailableMessage = () =>
  isDTS() ? `${settlement()} is not available for this ${entryLower()}.` : "Cash out is not available for this bet.";

export const settlementOfferLoadFailedMessage = () =>
  isDTS()
    ? `Unable to load the ${settlementLower()} offer. Please try again.`
    : "Unable to load cash out offer. Please try again.";

export const settlementOfferLabel = () => (isDTS() ? `${settlement()} offer now` : "Cash out offer now");

export const settlementSucceededMessage = () =>
  isDTS() ? `${settlement()} complete.` : "Cash out successful.";

export const settlementFailedMessage = ({ retry = false } = {}) => {
  if (isDTS()) return `${settlement()} failed.${retry ? " Please try again." : ""}`;
  return retry ? "Cash out failed. Please try again." : "Cash out failed.";
};

/** `amountText` is already formatted (coins on DTS, dollars on PPH). */
export const settlementConfirmLabel = (amountText) =>
  isDTS() ? `Confirm ${settlement()} — ${amountText}` : `Confirm Cash Out — ${amountText}`;

/** Closing sentence of the offer disclaimer. */
export const settlementIrreversibleNotice = () =>
  isDTS()
    ? `Once confirmed, this ${entryLower()} is settled immediately and cannot be reversed.`
    : "Once confirmed, this bet is settled immediately and cannot be reversed.";

/** Status shown for an entry that was settled early. */
export const settledEarlyStatus = () =>
  isDTS()
    ? { label: "Settled Early", detail: "Settled early at the live offer" }
    : { label: "Cashed Out", detail: "Settled early via cash out" };
